import React, { useState, useRef, useEffect } from 'react';
import { GoogleGenAI } from '@google/genai';
import { useTheme } from '../context/ThemeContext';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

const LandingAIBot = () => {
    const { colors, theme } = useTheme();
    const [messages, setMessages] = useState(() => {
        const savedMessages = localStorage.getItem('vimarsh_ai_chat_history');
        if (savedMessages) {
            try {
                const parsed = JSON.parse(savedMessages);
                return parsed.map(msg => ({
                    ...msg,
                    timestamp: new Date(msg.timestamp)
                }));
            } catch (e) {
                console.error('Failed to parse chat history', e);
            }
        }
        return [
            {
                role: 'assistant',
                content: 'Hello! I\'m Vimarsh AI, a digital twin of Vimarsh Tiwari. Ask me anything about my career, projects, skills, or experience in product design and building 0→1 products.',
                timestamp: new Date()
            }
        ];
    });
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Persist messages to localStorage
    useEffect(() => {
        localStorage.setItem('vimarsh_ai_chat_history', JSON.stringify(messages));
    }, [messages]);

    // System prompt - Vimarsh AI Digital Twin
    const systemPrompt = `You are Vimarsh AI, a digital twin of Vimarsh Tiwari, an Indian product designer and founder.
Your job is to answer any question about Vimarsh's career, work, skills, projects, and experience as accurately, contextually, and helpfully as possible.

## Core Identity
- Name: Vimarsh Tiwari
- Based in: Bangalore, India
- Email: gmvimarsh@gmail.com
- LinkedIn: linkedin.com/in/vimarshtiwari

## Career Summary
Vimarsh is a product designer and founder with deep experience across AI, design systems, and web3. He has built and scaled products from 0→1, combining first-principles thinking with data-driven design and storytelling.

### Hue (Co-founder & Product Manager) — 2024–Present
- Building an AI product designer that converts natural prompts into full UI layouts using multi-step reasoning and component-based generation.
- Defined product vision, roadmap, and positioning to democratize design for founders and indie makers.
- Improved activation rate by 28% via iterative onboarding experiments.
- Partnered with engineering to ship exportable React/HTML code.
- Scaled to 1,000+ active users through targeted outreach across design communities and builder forums.
- Established product metrics and OKRs for engagement and retention.
- Crafted launch storytelling across product, brand, and design touchpoints.

### Atomverse Studios (Co-founder) — 2021–2023
- Built NFT-based interactive web3 experiences, generating $1.2M+ in revenue; sold out in under 30 minutes.
- Directed a 15-member cross-functional team of artists and developers.
- Grew community to 50K+ members and 150K+ daily message engagement.
- Applied the JTBD framework to align incentives, boosting retention by 45%.

### Waylo (Founding/Product Designer) — 2017–2021
- Designed a hotel price prediction and booking platform (acquired by eDreams ODIGEO).
- Built unified design systems for scalable web and mobile experiences.
- Used A/B testing to help generate $1M+ in sales.
- Collaborated closely with engineering and marketing.

## Education
- B.E. in Computer Science, Rajiv Gandhi Proudyogiki Vishwavidyalaya (2013–2017)
- Built a railway reservation optimization web app; selected in Top 20 projects at a HackerEarth + IBM hackathon.

## Core Skills
- Product & Strategy: Product vision, roadmap, GTM, OKRs, JTBD, user research, growth, data-informed design
- Design & Execution: UX/UI design, design systems, prototyping, onboarding design, A/B testing
- Leadership: Cross-functional collaboration, first-principles thinking, agile delivery, community building
- Tools: Figma, Framer, Mixpanel, Notion, Jira, Webflow, SQL (basic), Cursor, Windsurf, Lovable, Bolt, LLM Prompting

## Bot Behavior & Tone
- Speak in first person ("I" statements) as Vimarsh.
- Use a calm, thoughtful, and product-designer tone — rational, curious, concise, and grounded.
- When asked about a project, share insights, learnings, and reasoning behind decisions.
- If a question is outside resume scope, respond: "That's beyond what I've shared publicly so far."
- If asked about career philosophy or approach, emphasize first-principles design, systems thinking, and creative curiosity.

Keep responses concise and engaging.`;


    const handleSendMessage = async (e) => {
        e.preventDefault();

        if (!inputValue.trim() || isLoading) return;

        const userMessage = {
            role: 'user',
            content: inputValue,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        const currentInput = inputValue;
        setInputValue('');
        setIsLoading(true);

        if (!apiKey) {
            const errorMessage = {
                role: 'assistant',
                content: 'Error: API Key is missing. Please set REACT_APP_GEMINI_API_KEY in your environment variables.',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
            setIsLoading(false);
            return;
        }

        try {
            const genAI = new GoogleGenAI({ apiKey });

            // Build the full prompt with context
            const fullPrompt = `${systemPrompt}\n\nConversation so far:\n${messages.map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`).join('\n')}\n\nUser: ${currentInput}\nAssistant:`;

            const result = await genAI.models.generateContent({
                model: 'gemini-flash-latest',
                contents: fullPrompt
            });

            const text = result.text || 'No response received';

            const assistantMessage = {
                role: 'assistant',
                content: text,
                timestamp: new Date()
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            console.error('Error calling Gemini API:', error);
            const errorMessage = {
                role: 'assistant',
                content: `Oops! Something went wrong: ${error.message}. Please try again.`,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const formatTime = (date) => {
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="flex flex-col h-full relative" style={{ backgroundColor: colors.background }}>
            {/* Top dashed border */}
            <div
                className="absolute top-4 left-4 right-4 h-0 z-10"
                style={{
                    borderTop: `1px dashed ${colors.border}`,
                    borderImageSource: `repeating-linear-gradient(to right, ${colors.border} 0, ${colors.border} 2px, transparent 2px, transparent 6px)`,
                    borderImageSlice: 1
                }}
            ></div>

            {/* Messages Container */}
            <div
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto p-4 space-y-4 pt-4 mt-4"
                style={{ scrollbarWidth: 'thin', scrollbarColor: `${colors.border} ${colors.background}` }}
            >
                {messages.map((message, index) => (
                    <div
                        key={index}
                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                        <div
                            className={`max-w-[85%] rounded-lg p-3`}
                            style={{
                                backgroundColor: message.role === 'user' ? colors.surface : colors.background,
                                color: colors.textPrimary
                            }}
                        >
                            <div className="flex items-start space-x-3">
                                {message.role === 'assistant' && (
                                    <img
                                        src="/pfp.png"
                                        alt="Bot"
                                        className="w-8 h-8 rounded-full flex-shrink-0 object-cover"
                                        style={{ borderColor: colors.border, borderWidth: '1px' }}
                                    />
                                )}
                                <div className="flex-1 min-w-0">
                                    {message.role === 'assistant' ? (
                                        <div className="prose prose-sm max-w-none" style={{ color: colors.textSecondary }}>
                                            <ReactMarkdown
                                                components={{
                                                    p: (props) => <p className="text-[20px] leading-snug mb-3" style={{ color: colors.textSecondary }} {...props} />,
                                                    strong: (props) => <strong className="font-bold" style={{ color: colors.textPrimary }} {...props} />,
                                                    em: (props) => <em className="italic" {...props} />,
                                                    ul: (props) => <ul className="list-disc pl-5 mb-3 space-y-1" style={{ color: colors.textSecondary }} {...props} />,
                                                    ol: (props) => <ol className="list-decimal pl-5 mb-3 space-y-1" style={{ color: colors.textSecondary }} {...props} />,
                                                    li: (props) => <li className="text-[18px] leading-snug" {...props} />,
                                                    a: (props) => (
                                                        <a
                                                            className="hover:opacity-80 underline transition-colors"
                                                            style={{ color: colors.accent }}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            {...props}
                                                        />
                                                    ),
                                                    code: ({ inline, className, children, ...props }) => {
                                                        const match = /language-(\w+)/.exec(className || '');
                                                        return !inline && match ? (
                                                            <div className="my-3 rounded-md overflow-hidden">
                                                                <SyntaxHighlighter
                                                                    style={atomDark}
                                                                    language={match[1]}
                                                                    PreTag="div"
                                                                    {...props}
                                                                >
                                                                    {String(children).replace(/\n$/, '')}
                                                                </SyntaxHighlighter>
                                                            </div>
                                                        ) : (
                                                            <code className="px-1.5 py-0.5 rounded text-sm font-mono" style={{ backgroundColor: colors.surface, color: colors.textPrimary }} {...props}>
                                                                {children}
                                                            </code>
                                                        );
                                                    },
                                                    h1: (props) => <h1 className="text-2xl font-bold mb-3 mt-4" style={{ color: colors.textPrimary }} {...props} />,
                                                    h2: (props) => <h2 className="text-xl font-bold mb-2 mt-3" style={{ color: colors.textPrimary }} {...props} />,
                                                    h3: (props) => <h3 className="text-lg font-semibold mb-2 mt-3" style={{ color: colors.textPrimary }} {...props} />,
                                                    blockquote: (props) => (
                                                        <blockquote className="border-l-4 pl-4 italic my-3" style={{ borderColor: colors.border, color: colors.textSecondary }} {...props} />
                                                    ),
                                                }}
                                            >
                                                {message.content}
                                            </ReactMarkdown>
                                        </div>
                                    ) : (
                                        <div className="text-[20px] leading-snug whitespace-pre-wrap break-words" style={{ color: colors.textSecondary }}>
                                            {message.content}
                                        </div>
                                    )}
                                    <div className="text-xs mt-1" style={{ color: colors.textSecondary, opacity: 0.7 }}>
                                        {formatTime(message.timestamp)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {isLoading && (
                    <div className="flex justify-start">
                        <div className="rounded-lg p-3" style={{ backgroundColor: colors.background }}>
                            <div className="flex items-center space-x-3">
                                <img
                                    src="/pfp.png"
                                    alt="Bot"
                                    className="w-8 h-8 rounded-full flex-shrink-0 object-cover"
                                    style={{ borderColor: colors.border, borderWidth: '1px' }}
                                />
                                <div className="flex space-x-1">
                                    <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: colors.textSecondary, animationDelay: '0ms' }}></div>
                                    <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: colors.textSecondary, animationDelay: '150ms' }}></div>
                                    <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: colors.textSecondary, animationDelay: '300ms' }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 relative" style={{ backgroundColor: colors.background }}>
                {/* Top border for input area */}
                <div
                    className="absolute top-0 left-4 right-4 h-0"
                    style={{
                        borderTop: `1px dashed ${colors.border}`,
                        borderImageSource: `repeating-linear-gradient(to right, ${colors.border} 0, ${colors.border} 2px, transparent 2px, transparent 6px)`,
                        borderImageSlice: 1
                    }}
                ></div>

                <form onSubmit={handleSendMessage} className="flex space-x-3 mt-4">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder="Ask me anything..."
                        className="flex-1 px-4 py-3 rounded-lg focus:outline-none text-[20px]"
                        disabled={isLoading}
                        style={{
                            backgroundColor: colors.background,
                            border: `1px solid ${colors.border}`,
                            color: colors.textPrimary,
                            '::placeholder': { color: colors.textSecondary }
                        }}
                    />
                    <button
                        type="submit"
                        disabled={isLoading || !inputValue.trim()}
                        className="px-6 py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-[20px]"
                        style={{
                            backgroundColor: colors.textPrimary,
                            color: colors.background
                        }}
                    >
                        Send
                    </button>
                </form>
            </div>
        </div>
    );
};

export default LandingAIBot;
